(() => {
  if (window.DiscountTierProgressInitialized) return;
  window.DiscountTierProgressInitialized = true;

  const SELECTOR = '.dtp';
  const CART_MUTATION_PATHS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];
  const THROTTLE_MS = 120;

  let refreshTimer = null;
  let requestId = 0;
  let abortController = null;

  const formatWithDelimiters = (number, precision, thousands, decimal) => {
    if (number === null || number === undefined) return '0';
    const num = Number(number) / 100;
    if (Number.isNaN(num)) return '0';
    const fixed = num.toFixed(precision);
    const parts = fixed.split('.');
    const integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const fraction = parts[1] ? decimal + parts[1] : '';
    return integer + fraction;
  };

  const formatMoney = (cents, format, currency) => {
    const value = typeof cents === 'string' ? cents.replace('.', '') : cents;
    const normalized = Number(value);
    if (Number.isNaN(normalized)) return '';

    if (!format) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'EUR',
      }).format(normalized / 100);
    }

    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'EUR',
      }).format(normalized / 100);
    }

    const placeholder = match[1];
    let formattedValue = '';
    switch (placeholder) {
      case 'amount':
        formattedValue = formatWithDelimiters(normalized, 2, '.', ',');
        break;
      case 'amount_no_decimals':
        formattedValue = formatWithDelimiters(normalized, 0, '.', ',');
        break;
      case 'amount_with_comma_separator':
        formattedValue = formatWithDelimiters(normalized, 2, '.', ',');
        formattedValue = formattedValue.replace(/\./g, 'X').replace(/,/g, '.').replace(/X/g, ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formattedValue = formatWithDelimiters(normalized, 0, '.', ',');
        formattedValue = formattedValue.replace(/\./g, 'X').replace(/,/g, '.').replace(/X/g, ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        formattedValue = formatWithDelimiters(normalized, 0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        formattedValue = formatWithDelimiters(normalized, 2, ' ', '.');
        break;
      default:
        formattedValue = formatWithDelimiters(normalized, 2, '.', ',');
        break;
    }

    return format.replace(match[0], formattedValue);
  };

  /**
   * Same tiers as Quick Buy tooltip + `snippets/discount-tier-progress.liquid`:
   * €1,000 / €1,500 / €2,000 (100000 / 150000 / 200000 cents).
   */
  const clampProgress = (n) => Math.min(100, Math.max(0, n));

  const formatProgressDisplay = (raw) => {
    const rounded = Math.round(clampProgress(raw) * 10) / 10;
    return rounded.toFixed(1);
  };

  /**
   * Exclusions (must match `snippets/discount-tier-progress.liquid` intent):
   * - Product IDs from the configured collection(s) (Liquid injects up to 250 per collection)
   * - Tag `discount-tier-exclude` on the product (from /products/{handle}.js or cart line if Shopify exposes product_tags)
   * - Never fall back to raw items_subtotal_price when exclusions apply — always sum lines after rules.
   *
   * Tier basis must match Shopify minimum-purchase evaluation: sum **pre-discount** line totals.
   * Ajax cart exposes this as `original_line_price` (see theme discounts docs). `line_price` can
   * match the discounted row total when automatic discounts apply — do not use it for tiers.
   */
  /** @param {Record<string, unknown>} line */
  function lineTierBasisCents(line) {
    const orig = Number(line.original_line_price);
    if (Number.isFinite(orig) && orig > 0) return orig;
    const lp = Number(line.line_price);
    return Number.isFinite(lp) && lp > 0 ? lp : 0;
  }

  function loadPromoExcludeConfig() {
    const el = document.querySelector('script[type="application/json"][data-dtp-promo-config]');
    if (!el || !el.textContent) return null;
    try {
      return JSON.parse(el.textContent.trim());
    } catch (e) {
      return null;
    }
  }

  let promoExcludeConfig = loadPromoExcludeConfig();

  const productJsonCache = new Map();
  let lastCartToken = '';

  function lineHasExcludeTag(line, excludeTag) {
    if (!excludeTag || !line) return false;
    const t = excludeTag.toLowerCase();
    if (Array.isArray(line.product_tags) && line.product_tags.some((x) => String(x).toLowerCase() === t)) {
      return true;
    }
    if (typeof line.product_tags === 'string' && line.product_tags.toLowerCase().includes(t)) {
      return true;
    }
    return false;
  }

  function normalizeTagsFromProductJson(p) {
    if (!p) return [];
    if (Array.isArray(p.tags)) return p.tags.map((x) => String(x).toLowerCase());
    if (typeof p.tags === 'string') {
      return p.tags
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
    }
    return [];
  }

  function fetchProductJson(handle) {
    if (!handle) return Promise.resolve(null);
    if (productJsonCache.has(handle)) return productJsonCache.get(handle);
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    const base = root.endsWith('/') ? root : `${root}/`;
    const p = fetch(`${base}products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
    productJsonCache.set(handle, p);
    return p;
  }

  async function getEligibleSubtotalCentsAsync(cart, cfg) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
      return 0;
    }

    const token = cart.token ? String(cart.token) : '';
    if (token !== lastCartToken) {
      productJsonCache.clear();
      lastCartToken = token;
    }

    const excludeTag = (cfg && cfg.excludeTag ? String(cfg.excludeTag) : 'discount-tier-exclude').toLowerCase();
    const promoIds = new Set((cfg && Array.isArray(cfg.productIds) ? cfg.productIds : []).map((id) => Number(id)));

    const handles = [...new Set(cart.items.map((l) => l.handle).filter(Boolean))];
    await Promise.all(handles.map((h) => fetchProductJson(h)));

    let sum = 0;
    for (const line of cart.items) {
      const pid = Number(line.product_id);
      const lineCents = lineTierBasisCents(line);

      // Matches main-cart-items: fee lines (often hidden) must not count toward discount tiers
      const pt = line.product_type ? String(line.product_type) : '';
      if (pt === 'mws_fee_generated') continue;

      if (lineHasExcludeTag(line, excludeTag)) continue;
      if (promoIds.has(pid)) continue;

      const pj = await fetchProductJson(line.handle);
      const tags = normalizeTagsFromProductJson(pj);
      if (tags.includes(excludeTag)) continue;

      sum += lineCents;
    }
    return sum;
  }

  const computeState = (subtotalCents) => {
    const cents = Math.max(0, Number(subtotalCents) || 0);

    if (cents <= 0) {
      return {
        kind: 'empty',
        isMax: false,
        progressPercent: 0,
        title: 'Discount tiers',
        subPlain: 'Add products to your cart to unlock discounts.',
      };
    }

    if (cents >= 200000) {
      return {
        kind: 'max',
        isMax: true,
        progressPercent: 100,
        title: 'Discount Level C reached - 3 % discount applied',
        subPlain: 'Maximum discount level reached.',
      };
    }

    if (cents >= 150000) {
      return {
        kind: 'level_b',
        isMax: false,
        missingCents: 200000 - cents,
        progressPercent: ((cents - 150000) / 50000) * 100,
        title: 'Discount Level B reached - 2 % discount applied',
      };
    }

    if (cents >= 100000) {
      return {
        kind: 'level_a',
        isMax: false,
        missingCents: 150000 - cents,
        progressPercent: ((cents - 100000) / 50000) * 100,
        title: 'Discount Level A reached - 1 % discount applied',
      };
    }

    return {
      kind: 'toward_a',
      isMax: false,
      missingCents: 100000 - cents,
      progressPercent: (cents / 100000) * 100,
      title: 'Next discount level: Reach Discount Level A for 1% off.',
    };
  };

  const render = async (cart) => {
    const containers = document.querySelectorAll(SELECTOR);
    if (!containers.length) return;

    if (!promoExcludeConfig) {
      promoExcludeConfig = loadPromoExcludeConfig();
    }
    let subtotal = 0;
    try {
      subtotal = await getEligibleSubtotalCentsAsync(cart, promoExcludeConfig);
    } catch (e) {
      subtotal = cart.items.reduce((s, line) => s + lineTierBasisCents(line), 0);
    }

    const currency =
      typeof cart.currency === 'string'
        ? cart.currency
        : cart.currency && cart.currency.iso_code
          ? cart.currency.iso_code
          : null;
    const state = computeState(subtotal);
    const progressPercentDisplay = formatProgressDisplay(state.progressPercent);
    const ariaValue = parseFloat(progressPercentDisplay);

    // #region agent log
    try {
      const sumLp = cart.items.reduce((s, l) => s + (Number(l.line_price) || 0), 0);
      const sumOl = cart.items.reduce((s, l) => s + (Number(l.original_line_price) || 0), 0);
      const first = cart.items[0];
      fetch('http://127.0.0.1:7494/ingest/16cde6c6-d2ca-47c9-aa08-7bef9f4460ec', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '29a4b8' },
        body: JSON.stringify({
          sessionId: '29a4b8',
          runId: 'tier-verify',
          hypothesisId: 'H1',
          location: 'discount-tier-progress.js:render',
          message: 'dtp tier basis vs line_price',
          data: {
            sumLinePrice: sumLp,
            sumOriginalLinePrice: sumOl,
            subtotalUsedForTiers: subtotal,
            dtpKind: state.kind,
            firstLine: first
              ? {
                  line_price: first.line_price,
                  original_line_price: first.original_line_price,
                  final_line_price: first.final_line_price,
                }
              : null,
          },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
    } catch (e) {}
    // #endregion

    containers.forEach((container) => {
      const moneyFormat =
        container.dataset.moneyFormat ||
        (window.Shopify && window.Shopify.money_format) ||
        null;

      const formattedMissing = formatMoney(state.missingCents, moneyFormat, currency);
      const titleEl = container.querySelector('.dtp__title');
      const subEl = container.querySelector('.dtp__sub');
      const percentEl = container.querySelector('.dtp__percent');
      const barEl = container.querySelector('.dtp__bar');
      const fillEl = container.querySelector('.dtp__fill');

      if (titleEl) {
        titleEl.textContent = state.title;
      }

      if (subEl) {
        if (state.kind === 'empty' || state.kind === 'max') {
          subEl.textContent = state.subPlain;
        } else if (state.kind === 'toward_a') {
          subEl.innerHTML = `Spend <strong class="dtp__sub-strong">${formattedMissing}</strong> more to unlock 1% off.`;
        } else if (state.kind === 'level_a') {
          subEl.innerHTML = `Spend <strong class="dtp__sub-strong">${formattedMissing}</strong> more to reach Discount Level B and get 2% discount.`;
        } else if (state.kind === 'level_b') {
          subEl.innerHTML = `Spend <strong class="dtp__sub-strong">${formattedMissing}</strong> more to reach Discount Level C and get 3% off.`;
        }
      }

      if (fillEl) {
        fillEl.style.width = `${progressPercentDisplay}%`;
      }

      if (barEl) {
        barEl.setAttribute('aria-valuenow', String(ariaValue));
      }

      if (percentEl) {
        percentEl.textContent = `${progressPercentDisplay}%`;
      }

      container.classList.toggle('is-max', state.isMax);
      if (state.kind) {
        container.dataset.dtpKind = state.kind;
      }
    });
  };

  const fetchCart = () => {
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();
    const currentRequest = ++requestId;

    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
      signal: abortController.signal,
    })
      .then((response) => response.json())
      .then((cart) => {
        if (currentRequest === requestId) {
          return render(cart);
        }
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
      });
  };

  const scheduleRefresh = () => {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
    }
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      fetchCart();
    }, THROTTLE_MS);
  };

  const isCartMutation = (url) => {
    if (!url) return false;
    const cleanUrl = typeof url === 'string' ? url : url.url;
    if (!cleanUrl) return false;
    return CART_MUTATION_PATHS.some((path) => cleanUrl.indexOf(path) !== -1);
  };

  const setupFetchInterceptor = () => {
    if (!window.fetch) return;
    const originalFetch = window.fetch.bind(window);
    window.fetch = (input, init) => {
      const isMutation = isCartMutation(input);
      return originalFetch(input, init).then((response) => {
        if (isMutation) {
          scheduleRefresh();
        }
        return response;
      });
    };
  };

  const setupInteractionHooks = () => {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form && form.matches && form.matches('form[action*="/cart"]')) {
        scheduleRefresh();
      }
    });

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!target) return;
      if (target.name === 'updates[]' || target.hasAttribute('data-quantity-variant-id')) {
        scheduleRefresh();
      }
    });

    document.addEventListener('shopify:section:load', scheduleRefresh);
    document.addEventListener('shopify:section:select', scheduleRefresh);
  };

  const init = () => {
    setupFetchInterceptor();
    setupInteractionHooks();
    fetchCart();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
